import { heightAt, inBounds, SIZE } from '../board.js';
import { ARMY, FORCE, MOVE_POINTS, WARRIOR_TYPES } from './config.js';
import { inTerritory, isHole, STEPS_8 } from './lake.js';
import type { IceAction, IceState, Slide, Square, Warrior, WarriorType } from './types.js';

/** A step costs 1 + the thickness difference, up or down. */
export function stepCost(from: number, to: number): number {
  return 1 + Math.abs(from - to);
}

/** A sliding warrior sinks if it lands this many levels above or below where it stood. */
export const SINK_DROP = 2;

export function warriorAt(state: IceState, r: number, c: number): Warrior | undefined {
  return state.warriors.find((w) => w.r === r && w.c === c);
}

export interface Reach {
  /** Cheapest cost per square (index `r * SIZE + c`), or Infinity. The start costs 0. */
  cost: number[];
  /** Index of the previous square on the cheapest path, or -1. */
  prev: number[];
}

/** Cheapest cost to reach (r, c), or Infinity. */
export function costTo(found: Reach, r: number, c: number): number {
  return found.cost[r * SIZE + c] ?? Infinity;
}

/** Cheapest walks over empty, non-hole squares within the warrior's move points. */
export function reach(state: IceState, warrior: Warrior): Reach {
  const points = MOVE_POINTS[warrior.type];
  const cost = new Array<number>(SIZE * SIZE).fill(Infinity);
  const prev = new Array<number>(SIZE * SIZE).fill(-1);
  const done = new Array<boolean>(SIZE * SIZE).fill(false);
  cost[warrior.r * SIZE + warrior.c] = 0;
  for (;;) {
    let best = -1;
    let bestCost = Infinity;
    cost.forEach((spent, i) => {
      if (!done[i] && spent <= points && spent < bestCost) {
        best = i;
        bestCost = spent;
      }
    });
    if (best < 0) return { cost, prev };
    done[best] = true;
    const br = Math.floor(best / SIZE);
    const bc = best % SIZE;
    const here = heightAt(state.heights, br, bc);
    for (const [dr, dc] of STEPS_8) {
      const r = br + dr;
      const c = bc + dc;
      if (!inBounds(r, c) || isHole(state.heights, r, c) || warriorAt(state, r, c)) continue;
      const next = bestCost + stepCost(here, heightAt(state.heights, r, c));
      if (next <= points && next < costTo({ cost, prev }, r, c)) {
        cost[r * SIZE + c] = next;
        prev[r * SIZE + c] = best;
      }
    }
  }
}

/** The cheapest path from the warrior's square to `to`, both included. */
export function pathTo(found: Reach, to: Square): Square[] {
  const path: Square[] = [];
  for (let i = to.r * SIZE + to.c; i >= 0; i = found.prev[i] ?? -1) {
    path.unshift({ r: Math.floor(i / SIZE), c: i % SIZE });
  }
  return path;
}

function touching(state: IceState, square: Square, side: Warrior['side'], except: string): number {
  return state.warriors.filter(
    (w) =>
      w.side === side &&
      w.id !== except &&
      Math.max(Math.abs(w.r - square.r), Math.abs(w.c - square.c)) === 1,
  ).length;
}

/**
 * Shove strength and shield wall when `shover`, from where it stands, shoves `target`.
 * Allies touching the target count on both sides; the shover itself never does.
 */
export function shoveOdds(
  state: IceState,
  shover: Warrior,
  target: Warrior,
): { strength: number; wall: number } {
  return {
    strength: FORCE[shover.type] + touching(state, target, shover.side, shover.id),
    wall: FORCE[target.type] + touching(state, target, target.side, target.id),
  };
}

/**
 * The warriors in line from `target` away from `from` that slide one square, farthest first.
 * The push stops at a warrior that is crushed against a wall or falls from a ledge (2+ levels
 * up or down): it is lost, and whoever stands on that wall or below that ledge doesn't move.
 */
export function shoveSlides(state: IceState, from: Square, target: Square): Slide[] {
  const dr = target.r - from.r;
  const dc = target.c - from.c;
  const slides: Slide[] = [];
  let at: Square = { r: target.r, c: target.c };
  for (let w = warriorAt(state, at.r, at.c); w;) {
    const to = { r: at.r + dr, c: at.c + dc };
    const onIce = inBounds(to.r, to.c) && !isHole(state.heights, to.r, to.c);
    const steep =
      onIce &&
      Math.abs(heightAt(state.heights, to.r, to.c) - heightAt(state.heights, at.r, at.c)) >=
        SINK_DROP;
    slides.push({ id: w.id, from: at, to, sunk: !onIce || steep });
    // Off the lake or into a hole, nobody is beyond; against a wall or off a ledge, the push
    // stops there.
    if (!onIce || steep) break;
    at = to;
    w = warriorAt(state, at.r, at.c);
  }
  return slides.reverse();
}

/** What a shove would do, for hints. `legal` is false when the shield wall holds. */
export interface ShoveOption {
  action: Extract<IceAction, { kind: 'shove' }>;
  /**
   * The whole route the shover takes: its square, the walk to `from`, then the target's square.
   * The last step gives the shove its direction.
   */
  path: Square[];
  /** One step in the direction the line slides, e.g. `{ dr: -1, dc: 1 }` toward rank 9, file i. */
  direction: { dr: number; dc: number };
  strength: number;
  wall: number;
  legal: boolean;
  slides: Slide[];
}

/** `state` with warrior `id` standing on `to`: the shover has left its square before it shoves. */
export function walked(state: IceState, id: string, to: Square): IceState {
  return {
    ...state,
    warriors: state.warriors.map((w) => (w.id === id ? { ...w, r: to.r, c: to.c } : w)),
  };
}

/**
 * The shove `warrior` would make by walking along `walk` (ending on the approach square) and
 * stepping into `target`. Doesn't check that the walk is affordable.
 */
export function shoveFrom(
  state: IceState,
  warrior: Warrior,
  walk: Square[],
  target: Square,
): ShoveOption | null {
  const from = walk.at(-1) ?? { r: warrior.r, c: warrior.c };
  const there = walked(state, warrior.id, from);
  const enemy = warriorAt(there, target.r, target.c);
  if (!enemy || enemy.side === warrior.side) return null;
  const { strength, wall } = shoveOdds(there, { ...warrior, ...from }, enemy);
  return {
    action: { kind: 'shove', id: warrior.id, from, target: { r: target.r, c: target.c } },
    path: [...walk, { r: target.r, c: target.c }],
    direction: { dr: target.r - from.r, dc: target.c - from.c },
    strength,
    wall,
    legal: strength >= wall,
    slides: shoveSlides(there, from, target),
  };
}

/** Every shove `warrior` can reach, whether or not it would work. */
export function shoveOptions(state: IceState, warrior: Warrior): ShoveOption[] {
  const points = MOVE_POINTS[warrior.type];
  const found = reach(state, warrior);
  const options: ShoveOption[] = [];
  for (let r = 0; r < SIZE; r++) {
    for (let c = 0; c < SIZE; c++) {
      const spent = costTo(found, r, c);
      if (spent > points) continue;
      const walk = pathTo(found, { r, c });
      for (const [dr, dc] of STEPS_8) {
        const target = warriorAt(state, r + dr, c + dc);
        if (!target || target.side === warrior.side || target.id === warrior.id) continue;
        const last = stepCost(
          heightAt(state.heights, r, c),
          heightAt(state.heights, target.r, target.c),
        );
        if (spent + last > points) continue;
        const option = shoveFrom(state, warrior, walk, target);
        if (option) options.push(option);
      }
    }
  }
  return options;
}

/** Warriors of each type `side` still has to place. */
export function toPlace(state: IceState, side: Warrior['side']): Record<WarriorType, number> {
  const left = { ...ARMY };
  for (const w of state.warriors) if (w.side === side) left[w.type]--;
  return left;
}

/** Legal actions for the side to move. A pass is legal only when nothing else is. */
export function legalIceActions(state: IceState): IceAction[] {
  if (state.phase === 'over') return [];
  const side = state.turn;
  const actions: IceAction[] = [];
  if (state.phase === 'placing') {
    const left = toPlace(state, side);
    for (const type of WARRIOR_TYPES) {
      if (left[type] === 0) continue;
      for (let r = 0; r < SIZE; r++) {
        if (!inTerritory(side, r)) continue;
        for (let c = 0; c < SIZE; c++) {
          if (!isHole(state.heights, r, c) && !warriorAt(state, r, c)) {
            actions.push({ kind: 'place', type, at: { r, c } });
          }
        }
      }
    }
    return actions;
  }
  for (const warrior of state.warriors) {
    if (warrior.side !== side) continue;
    const found = reach(state, warrior);
    for (let r = 0; r < SIZE; r++) {
      for (let c = 0; c < SIZE; c++) {
        const spent = costTo(found, r, c);
        if (spent > 0 && spent !== Infinity) {
          actions.push({ kind: 'move', id: warrior.id, to: { r, c } });
        }
      }
    }
    for (const option of shoveOptions(state, warrior)) {
      if (option.legal) actions.push(option.action);
    }
  }
  return actions.length > 0 ? actions : [{ kind: 'pass' }];
}
